import React, { useState, useCallback } from 'react';
import { Sparkles, Wrench } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button, Input, Label, NNSelect, ICON_PX,
} from '../ui';

const DOMAIN_OPTIONS = [
  { value: 'safety', label: 'Safety' },
  { value: 'quality', label: 'Quality' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'staffing', label: 'Staffing' },
  { value: 'access', label: 'Access' },
  { value: 'finance', label: 'Finance' },
  { value: 'operations', label: 'Operations' },
  { value: 'communications', label: 'Communications' },
];

const CADENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
  { value: 'as_needed', label: 'As needed' },
];

const REGULATORY_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'osha', label: 'OSHA' },
  { value: 'joint_commission', label: 'Joint Commission' },
  { value: 'cms', label: 'CMS' },
];

/**
 * Modal dialog for creating a new workflow template.
 * Offers two paths:
 *   1. "Start from Scratch" → empty canvas with build mode
 *   2. "Describe to AI" → AI generates workflow from description
 */
function CreateWorkflowDialog({ open, onClose, onCreate, onCreateWithAI }) {
  const [step, setStep] = useState('choose'); // 'choose' | 'form' | 'ai'
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [cadence, setCadence] = useState('');
  const [regulatorySource, setRegulatorySource] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const valid = name.trim() && domain && cadence;
  const aiValid = valid && aiPrompt.trim();

  const handleClose = useCallback(() => {
    setStep('choose');
    setName('');
    setDomain('');
    setCadence('');
    setRegulatorySource('');
    setAiPrompt('');
    setSubmitting(false);
    onClose?.();
  }, [onClose]);

  // Path A: Start from Scratch
  const handleCreateScratch = useCallback(async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    const endNodeId = `end-${Date.now()}`;
    const workflowData = {
      name: name.trim(),
      domain,
      cadence,
      regulatorySource: regulatorySource || null,
      nodes: [{
        id: endNodeId,
        type: 'end',
        position: { x: 250, y: 100 },
        label: 'Finish',
      }],
      edges: [],
    };
    try {
      await onCreate?.(workflowData);
      handleClose();
    } catch (err) {
      setSubmitting(false);
    }
  }, [valid, submitting, name, domain, cadence, regulatorySource, onCreate, handleClose]);

  // Path B: Describe to AI
  const handleCreateWithAI = useCallback(async () => {
    if (!aiValid || submitting) return;
    setSubmitting(true);
    try {
      await onCreateWithAI?.({
        name: name.trim(),
        domain,
        cadence,
        regulatorySource: regulatorySource || null,
        prompt: aiPrompt.trim(),
      });
      handleClose();
    } catch (err) {
      setSubmitting(false);
    }
  }, [aiValid, submitting, name, domain, cadence, regulatorySource, aiPrompt, onCreateWithAI, handleClose]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Workflow</DialogTitle>
        </DialogHeader>

        {/* Step 1: Choose path */}
        {step === 'choose' && (
          <div className="px-6 py-6 space-y-3">
            <p className="text-sm text-slate-500 mb-4">How would you like to create this workflow?</p>

            <button
              type="button"
              onClick={() => setStep('form')}
              className="w-full flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-[var(--aqua)] hover:bg-[var(--aqua)]/5 transition-colors text-left group"
            >
              <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-[var(--aqua)]/10 flex items-center justify-center shrink-0">
                <Wrench size={ICON_PX.lg} className="text-slate-500 group-hover:text-[var(--aqua)]" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-text-dark">Start from Scratch</div>
                <div className="text-xs text-slate-400">Build the workflow manually using the canvas editor</div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setStep('ai')}
              className="w-full flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-[var(--aqua)] hover:bg-[var(--aqua)]/5 transition-colors text-left group"
            >
              <div className="w-10 h-10 rounded-lg bg-[var(--aqua)]/10 flex items-center justify-center shrink-0">
                <Sparkles size={ICON_PX.lg} className="text-[var(--aqua)]" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-text-dark">Describe to AI</div>
                <div className="text-xs text-slate-400">Describe what you need and AI will generate it for you</div>
              </div>
            </button>
          </div>
        )}

        {/* Step 2a: Manual form */}
        {step === 'form' && (
          <>
            <div className="px-6 py-4 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Monthly Safety Inspection"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Category *</Label>
                <NNSelect value={domain} onChange={setDomain} options={DOMAIN_OPTIONS} placeholder="Choose a category" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">How often? *</Label>
                <NNSelect value={cadence} onChange={setCadence} options={CADENCE_OPTIONS} placeholder="Choose a cadence" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Regulatory source (optional)</Label>
                <NNSelect value={regulatorySource} onChange={setRegulatorySource} options={REGULATORY_OPTIONS} placeholder="None" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep('choose')} disabled={submitting}>Back</Button>
              <Button variant="save" onClick={handleCreateScratch} disabled={!valid || submitting}>
                {submitting ? 'Creating...' : 'Create & Edit'}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step 2b: AI form */}
        {step === 'ai' && (
          <>
            <div className="px-6 py-4 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Monthly Safety Inspection"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-600">Category *</Label>
                  <NNSelect value={domain} onChange={setDomain} options={DOMAIN_OPTIONS} placeholder="Category" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-600">How often? *</Label>
                  <NNSelect value={cadence} onChange={setCadence} options={CADENCE_OPTIONS} placeholder="Cadence" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Describe your workflow *</Label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="e.g., I need a quarterly OSHA compliance workflow. An RN should do hazard assessment and PPE checks in parallel. The office manager reviews findings and either creates a corrective action plan or signs off on compliance. Add a 48h SLA on the hazard assessment."
                  rows={4}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-text-dark placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-aqua/40 resize-none"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep('choose')} disabled={submitting}>Back</Button>
              <Button variant="save" onClick={handleCreateWithAI} disabled={!aiValid || submitting}>
                <Sparkles size={ICON_PX.sm} className="mr-1" />
                {submitting ? 'Generating...' : 'Generate Workflow'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CreateWorkflowDialog;
